import { useCallback, useState } from 'preact/hooks';

/**
 * Page-local state that survives navigating away and back (pasted chains, form inputs, results).
 * Kept in memory only — a reload starts clean, and nothing here is serialised, so results holding
 * Uint8Arrays are fine. Keys are per page (and per log where it matters), e.g. 'submit:text'.
 */
const store = new Map<string, unknown>();

export function usePageState<T>(key: string, initial: T | (() => T)): [T, (next: T | ((prev: T) => T)) => void] {
  const [value, setValue] = useState<T>(() => (store.has(key) ? (store.get(key) as T) : typeof initial === 'function' ? (initial as () => T)() : initial));
  const set = useCallback(
    (next: T | ((prev: T) => T)) => {
      setValue((prev) => {
        const v = typeof next === 'function' ? (next as (p: T) => T)(prev) : next;
        store.set(key, v);
        return v;
      });
    },
    [key],
  );
  return [value, set];
}

/** Forget remembered page state (all of it, or every key under a prefix such as 'submit:'). */
export function clearPageState(prefix = ''): void {
  for (const k of [...store.keys()]) if (k.startsWith(prefix)) store.delete(k);
}
